import type {City} from "@/constants/cities.ts";
import {useEffect, useState} from "react";

/**
 * Hook personnalisé pour gérer des données JSON dans localStorage pour une clé donnée.
 * Fournit des helpers pour récupérer, ajouter et réinitialiser les données.
 * @param {string} key - Clé utilisée dans localStorage. "city" par défaut
 */
export function useCitiesStorage(key: string = "city") {
    const [data, setData] = useState(localStorage.getItem(key))

    useEffect(() => {
        setData(localStorage.getItem(key))
    }, [localStorage.getItem(key)]);

    /**
     * Récupère la valeur parsée depuis `data`
     */
    const get = () => data ? (JSON.parse(data) as City[]) : null

    /**
     * Ajoute une `city` à la liste stockée si elle n'existe pas déjà.
     * Si aucune liste n'existe encore, crée un nouveau tableau contenant la city.
     */
    const set = (city: City) => {
        const data = get()
        if (data) {
            const exists = data.find(d => d.name.toLowerCase() === city.name.toLowerCase())
            if (!exists) localStorage.setItem(key, JSON.stringify([...data, city]))
        } else localStorage.setItem(key, `[${JSON.stringify(city)}]`)
    }

    /**
     * Ajoute une pile de ville dans la storage sans verification
     */
    const setAll = (cities: City[]) => {
        localStorage.setItem(key, JSON.stringify(cities))
    }

    /**
     * Retirer une ville du storage
     * Renvoie une erreur si la ville spécifier n'est pas retrouvé dans la storage
     * @param {string} cityName - Nom de la ville
     */
    const remove = (cityName: string) => {
        const cities = get()
        if (!cities) return
        const index = cities.findIndex(c => c.name.toLowerCase() === cityName.toLowerCase())
        if (index === -1) throw new Error("La ville n'existe pas dans l'espace de stockage")
        const newCities = cities.filter(c => c.name.toLowerCase() !== cityName.toLowerCase())
        if (newCities.length === 0) localStorage.setItem(key, "")
        else setAll(newCities)
    }

    return {
        set, setAll, get, remove
    }
}